import * as d3 from 'd3';
import React from 'react';

import Axis from 'src/components/D3Axis';
import {LegendConfig, makeLine, makeRect} from 'src/components/Legend';
import {useTranslations} from 'src/components/TranslationsContext';
import WithWidth from 'src/components/WithWidth';
import {
  EcmwfOutlook,
  GefsOutlook,
  WeeklyOutlookBlock,
  WeeklyStat,
} from 'src/lib/api/regionalReport';
import {Dims} from 'src/lib/commonTypes';
import {getGraphDims} from 'src/lib/vizUtils';

import {
  ECMWF_FORECAST_NAME,
  GEFS_ANALYSIS_NAME,
  GEFS_FORECAST_NAME,
  upcomingBlocks,
} from './commentary';
import {StaticLegend} from './ForecastEvolutionChart';

const PANEL_HEIGHT = 210;
const PANEL_MARGIN = {top: 12, right: 10, bottom: 46, left: 64};
const NORMAL_COLOR = '#111827';
const GEFS_COLOR = '#2563eb';
const ECMWF_COLOR = '#93c5fd';

type Channel = 'precipitation' | 'temperature';
type Week = {
  start: string;
  gefs?: WeeklyOutlookBlock;
  ecmwf?: WeeklyOutlookBlock;
};

function utcDate(day: string): Date {
  return new Date(`${day}T00:00:00Z`);
}

export function weekLabel(block: WeeklyOutlookBlock): string[] {
  const start = utcDate(block.start);
  const end = utcDate(block.end);
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  const range = `${d3.utcFormat('%b %-d')(start)}–${d3.utcFormat(sameMonth ? '%-d' : '%b %-d')(end)}`;
  const notes = [];
  if (block.dayCount < 7) notes.push(`${block.dayCount} days`);
  if (block.elapsedDays > 0 && block.elapsedDays < block.dayCount) notes.push('partly past');
  return notes.length ? [range, `(${notes.join(', ')})`] : [range];
}

function mergeWeeks(gefs: WeeklyOutlookBlock[], ecmwf: WeeklyOutlookBlock[]): Week[] {
  const byStart = new Map<string, Week>();
  gefs.forEach((block) => byStart.set(block.start, {start: block.start, gefs: block}));
  ecmwf.forEach((block) =>
    byStart.set(block.start, {...byStart.get(block.start), start: block.start, ecmwf: block})
  );
  return Array.from(byStart.values())
    .filter((week) => upcomingBlocks([week.gefs, week.ecmwf].filter(isBlock)).length > 0)
    .map((week) => ({
      ...week,
      gefs: upcomingBlocks(week.gefs && [week.gefs])[0],
      ecmwf: upcomingBlocks(week.ecmwf && [week.ecmwf])[0],
    }))
    .sort((a, b) => a.start.localeCompare(b.start));
}

function isBlock(block: WeeklyOutlookBlock | undefined): block is WeeklyOutlookBlock {
  return block != null;
}

function Range({
  cx,
  width,
  stat,
  yScale,
  color,
  hollowMean = false,
}: {
  cx: number;
  width: number;
  stat: WeeklyStat;
  yScale: d3.ScaleLinear<number, number>;
  color: string;
  hollowMean?: boolean;
}) {
  return (
    <g>
      {stat.p10 != null && stat.p90 != null && (
        <rect
          x={cx - width / 2}
          y={yScale(stat.p90)}
          width={width}
          height={Math.max(1, yScale(stat.p10) - yScale(stat.p90))}
          fill={color}
          fillOpacity={0.45}
          stroke={color}
        />
      )}
      {stat.normal != null && (
        <line
          x1={cx - width / 2 - 3}
          x2={cx + width / 2 + 3}
          y1={yScale(stat.normal)}
          y2={yScale(stat.normal)}
          stroke={NORMAL_COLOR}
          strokeWidth={2}
        />
      )}
      {stat.mean != null && (
        <circle
          cx={cx}
          cy={yScale(stat.mean)}
          r={3.5}
          fill={hollowMean ? 'white' : color}
          stroke="#111827"
          strokeWidth={0.8}
        />
      )}
    </g>
  );
}

function Panel({
  weeks,
  channel,
  unit,
  title,
  width,
}: {
  weeks: Week[];
  channel: Channel;
  unit: string;
  title: string;
  width: number;
}) {
  const dims: Dims = [width, PANEL_HEIGHT];
  const [graphW, graphH] = getGraphDims(dims, PANEL_MARGIN);
  const values = weeks.flatMap((week) =>
    [week.gefs?.[channel], week.ecmwf?.[channel]].flatMap((stat) =>
      stat ? [stat.p10, stat.p90, stat.mean, stat.normal] : []
    )
  );
  const defined = values.filter((v): v is number => v != null);
  const low = channel === 'precipitation' ? 0 : (d3.min(defined) ?? 0);
  const high = d3.max(defined) ?? 1;
  const pad = (high - low) * 0.08 || 1;
  const yScale = d3
    .scaleLinear()
    .domain([channel === 'precipitation' ? 0 : low - pad, high + pad])
    .range([graphH, 0])
    .nice();
  const xScale = d3
    .scaleBand()
    .domain(weeks.map((w) => w.start))
    .range([0, graphW])
    .paddingInner(0.25)
    .paddingOuter(0.1);
  const barW = xScale.bandwidth() * 0.32;

  return (
    <svg width={dims[0]} height={dims[1]}>
      <text x={PANEL_MARGIN.left} y={9} fontSize={11} fontWeight={600} fill="#374151">
        {title}
      </text>
      <g transform={`translate(${PANEL_MARGIN.left},${PANEL_MARGIN.top})`}>
        {weeks.map((week) => {
          const cx = (xScale(week.start) ?? 0) + xScale.bandwidth() / 2;
          const label = weekLabel((week.gefs ?? week.ecmwf)!);
          return (
            <g key={week.start}>
              {week.gefs && (
                <Range
                  cx={week.ecmwf ? cx - barW / 2 - 2 : cx}
                  width={barW}
                  stat={week.gefs[channel]}
                  yScale={yScale}
                  color={GEFS_COLOR}
                />
              )}
              {week.ecmwf && (
                <Range
                  cx={week.gefs ? cx + barW / 2 + 2 : cx}
                  width={barW}
                  stat={week.ecmwf[channel]}
                  yScale={yScale}
                  color={ECMWF_COLOR}
                  hollowMean={!week.gefs}
                />
              )}
              <text x={cx} y={graphH + 14} textAnchor="middle" fontSize={9} fill="#374151">
                {label.map((line, i) => (
                  <tspan
                    key={i}
                    x={cx}
                    dy={i === 0 ? 0 : 11}
                    fill={i === 0 ? '#374151' : '#6b7280'}
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}
        <line x1={0} x2={graphW} y1={graphH} y2={graphH} stroke="#9ca3af" />
        <Axis
          axisCreator={d3.axisLeft(yScale).ticks(5)}
          yLabelProps={{graphDims: [graphW, graphH], text: unit}}
        />
      </g>
    </svg>
  );
}

export default function WeeklyOutlookChart({
  gefs,
  ecmwf,
}: {
  gefs: GefsOutlook | null;
  ecmwf: EcmwfOutlook | null;
}) {
  const {t} = useTranslations();
  const weeks = mergeWeeks(gefs?.weekly ?? [], ecmwf?.weekly ?? []);
  const units = gefs?.weeklyUnits ?? ecmwf?.weeklyUnits;
  if (!weeks.length || !units) return null;
  const climatologyYears = gefs?.climatologyYears ?? ecmwf?.climatologyYears;

  const legendConfigs: LegendConfig[] = [
    ...(weeks.some((week) => week.gefs)
      ? [
          {
            id: 'gefs',
            display: GEFS_FORECAST_NAME,
            makeIcon: () => makeRect({fill: GEFS_COLOR, fillOpacity: 0.45}),
          },
        ]
      : []),
    ...(weeks.some((week) => week.ecmwf)
      ? [
          {
            id: 'ecmwf',
            display: ECMWF_FORECAST_NAME,
            makeIcon: () => makeRect({fill: ECMWF_COLOR, fillOpacity: 0.45}),
          },
        ]
      : []),
    {
      id: 'normal',
      display: climatologyYears
        ? `${climatologyYears[0]}–${climatologyYears[1]} ${GEFS_ANALYSIS_NAME} average`
        : `${GEFS_ANALYSIS_NAME} average`,
      makeIcon: () => makeLine({stroke: NORMAL_COLOR, strokeWidth: 2}),
    },
  ];

  return (
    <div>
      <WithWidth>
        {(width) => (
          <div>
            <Panel
              weeks={weeks}
              channel="precipitation"
              unit={units.precipitation}
              title={t('regionalReportWeeklyPrecipitation')}
              width={width}
            />
            <Panel
              weeks={weeks}
              channel="temperature"
              unit={units.temperature}
              title={t('regionalReportWeeklyTemperature')}
              width={width}
            />
          </div>
        )}
      </WithWidth>
      <StaticLegend configs={legendConfigs} />
    </div>
  );
}
